import React from 'react';
import {
    Box,
    Button,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    Select,
    MenuItem,
    TextField,
    Checkbox,
    FormControlLabel,
} from '@mui/material';

import RenderIf from '../../renderIf';
import DatePicker from '../../shared/datePick/DatePick';
import { RequiredMarker } from '../../shared/muiControls';

import useChangeApplicationStatus from './useChangeApplicationStatus';

/**A Modal component that facilitates the changing of an Application Instance's Status, and any required data points that might go along with the new status.
 * 
 * @param {*} appId The Application Instance ID for the Application whose Status is being changed.
 * @param {boolean} open Controls whether the Dialog is open or closed.
 * @param {function} handleClose Handler function for closing the window with no resultant actions, i.e. cancel.
 * @param {function} handleSuccess Handler function for successful status change and resultant actions, i.e. page refresh.
 * @param {ReactElement} StatusComponent The styled status component to display.
 * @param {integer} currentStatus The current Status ID for the application, used in retrieving what eligible statuses the application can be moved to.
 * @param {object} userSettings The userSettings object from the ProtectedRouteContext, prevents another import of it when we already have it.
*/
const ChangeApplicationStatus = ({
    appId,
    open = true,
    handleClose = () => null,
    handleSuccess = () => null,
    StatusComponent = () => null,
    currentStatus,
    userSettings,
    ...restOfProps
}) => {
    //#region Hooks and States
    const {
        styles,
        //UI states
        isInitialized,
        isDoingAjax,
        statusOptions,
        inError,
        showValidation,
        //User-driven values
        newStatus,
        approvedDate,
        changeReason,
        suppressEmail,
        //Display-only values
        maxScholarshipAmt,
        netScholarshipAmtCalc,
        adminFee,
        //Functions
        initializeStatusChange,
        saveChanges,
        resetModal,
        handleDateChange,
        handleDateError,
        handleStatusChange,
        handleChangeReasonChange,
        handleSuppressEmailChange,
        translateCurrency,
    } = useChangeApplicationStatus(appId, userSettings);
    //#endregion Hooks and States

    //#region Handler functions
    const closeModal = async () => {
        resetModal?.();
        handleClose?.();
    };

    const handleUpdate = async () => {
        let success = await saveChanges?.();
        if (success) {
            handleSuccess?.();
            resetModal?.();
            handleClose?.();
        }
    };
    //#endregion Handler functions

    //#region Effects
    React.useEffect(() => {
        if (open && currentStatus) {
            initializeStatusChange(currentStatus, closeModal);
        }
    }, [open, currentStatus]);
    //#endregion Effects

    return (
        <Dialog open={open}>
            <DialogTitle>Change application status</DialogTitle>

            <DialogContent>
                <Typography sx={styles.instructionsSx}>
                    Select a new status and click "Update Status" to save. The current status is: {StatusComponent?.()}
                </Typography>

                <Typography sx={styles.fieldLabelSx}>
                    <RequiredMarker /> New Status
                </Typography>

                <Select
                    value={newStatus}
                    onChange={(event) => handleStatusChange(event?.target?.value || null)}
                    error={showValidation && inError.includes('newStatus')}
                    disabled={isDoingAjax || !isInitialized}
                    size="small"
                    sx={styles.selectSx}
                >
                    <MenuItem
                        sx={(theme) => {
                            return {
                                ...styles.selectSx(theme),
                                height: '2.25rem',
                            };
                        }}
                        value={-1}
                    ></MenuItem>
                    {statusOptions?.map?.((obj) => {
                        return (
                            <MenuItem
                                value={obj.StatusID}
                                sx={styles.selectSx}
                            >
                                {obj.Description}
                            </MenuItem>
                        );
                    })}
                </Select>

                {
                    //#region Approved
                }
                <RenderIf isTrue={newStatus === 6}>
                    <Typography
                        sx={(theme) => {
                            return {
                                ...styles.fieldLabelSx(theme),
                                mt: theme.spacing(3),
                            };
                        }}
                    >
                        <RequiredMarker /> Date Approved
                    </Typography>
                    <DatePicker
                        value={approvedDate}
                        onChange={(newVal) => handleDateChange(newVal)}
                        onError={(err, newVal) => handleDateError(err, newVal)}
                        error={showValidation && inError.includes('approvedDate')}
                        disabled={isDoingAjax || !isInitialized}
                        size="small"
                        width="12.625rem"
                        clearable
                        textFieldSx={styles.dateFieldSx}
                    />
                </RenderIf>
                {
                    //#endregion Approved
                }

                {
                    //#region Pending Acceptance
                }
                <RenderIf isTrue={newStatus === 8}>
                    <Box sx={styles.scholarshipDataRowSx}>
                        <Box>
                            <Typography sx={styles.fieldLabelSx}>Max Scholarship Amt</Typography>

                            <Typography sx={styles.scholarshipDataReadOnlySx}>{translateCurrency(maxScholarshipAmt)}</Typography>
                        </Box>

                        <Box>
                            <Typography sx={styles.fieldLabelSx}>Administrative Fee</Typography>

                            <Typography sx={styles.scholarshipDataReadOnlySx}>{translateCurrency(adminFee)}</Typography>
                        </Box>

                        <Box>
                            <Typography sx={styles.fieldLabelSx}>Net Scholarship Amt</Typography>

                            <Typography sx={styles.scholarshipDataReadOnlySx}>{translateCurrency(netScholarshipAmtCalc)}</Typography>
                        </Box>
                    </Box>
                </RenderIf>
                {
                    //#endregion Pending Acceptance
                }

                <Typography
                    sx={(theme) => {
                        return {
                            ...styles.fieldLabelSx(theme),
                            mt: theme.spacing(3),
                        };
                    }}
                >
                    <RenderIf isTrue={[3, 5].includes(newStatus)}>
                        <RequiredMarker />{' '}
                    </RenderIf>
                    Reason for Change
                </Typography>
                <TextField
                    value={changeReason}
                    onChange={(event) => handleChangeReasonChange(event?.target?.value || '')}
                    error={showValidation && inError.includes('changeReason')}
                    multiline
                    rows={5}
                    disabled={isDoingAjax || !isInitialized}
                    sx={styles.textFieldSx}
                />

                <FormControlLabel
                    label="Do not send email notifications"
                    control={
                        <Checkbox
                            checked={suppressEmail}
                            onChange={(event) => {
                                handleSuppressEmailChange(event?.target?.checked);
                            }}
                            inputProps={{
                                'aria-label': 'Do not send email',
                            }}
                            size="small"
                            disabled={isDoingAjax || !isInitialized}
                        />
                    }
                    sx={styles.checkboxSx}
                />

                <Box>
                    <Button
                        onClick={closeModal}
                        disabled={isDoingAjax || !isInitialized}
                    >
                        Close
                    </Button>

                    <Button
                        variant="contained"
                        onClick={handleUpdate}
                        disabled={isDoingAjax || !isInitialized}
                    >
                        Update Status
                    </Button>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default ChangeApplicationStatus;

